import { Group, SegmentedControl, Switch, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';
import { DeviceStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconDeviceTablet } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

import { api } from '../../api/client';
import { deviceCountsQuery, deviceQuery, meQuery } from '../../api/hooks';
import { AcrossList, type Column } from '../../components/across';
import { StatusMark } from '../../components/device-status';
import { useShown } from '../../components/dialog';
import { IntegrityFlag } from '../../components/integrity-flag';
import { useServerPages } from '../../components/pager';
import { devicesIn } from '../../lib/counts';
import { PaneDrawer } from '../../components/pane';
import { isStaff } from '../../lib/roles';
import { DeviceActions, DeviceName, DevicePane, LastSeen, LastSync, OrglessBadge } from './devices';

// Every device the console knows, for MotionSafe staff: the ones assigned to
// an organization and the ones that belong to none, in one list. Those
// waiting on a person come first: a reset tablet that needs approval, then the
// unassigned.
export const Route = createFileRoute('/_app/all-devices')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: AllDevices,
});

type Show = 'all' | 'unassigned' | 'assigned';

// The organization's own table, with the organization beside the device:
// the same name, ending and serial, the same last seen and last sync.
const COLUMNS: Column<Device>[] = [
  { head: 'Device', cell: (d) => <DeviceName d={d} /> },
  { head: 'Status', width: 72, cell: (d) => <StatusMark d={d} /> },
  { head: 'Integrity', width: 84, cell: (d) => <IntegrityFlag d={d} /> },
  { head: 'Model', width: 110, cell: (d) => d.model || <Text span inherit c="dimmed">unknown</Text> },
  { head: 'App', width: 90, cell: (d) => <span className="id">{d.appVersion || <Text span inherit c="dimmed">unknown</Text>}</span> },
  { head: 'Last seen', width: 110, cell: (d) => <LastSeen d={d} /> },
  { head: 'Last sync', width: 110, cell: (d) => <LastSync d={d} /> },
];

function AllDevices() {
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 200);
  // Clearing applies at once; typing waits for a pause.
  const term = q.trim() ? debounced.trim() : '';
  const [show, setShow] = useState<Show>('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);
  const list = useServerPages({
    key: ['devices', 'all'],
    narrowed: `${show}|${flaggedOnly}|${term}`,
    read: (page) => api.device.listDevices({
      orgId: '',
      ...(show === 'unassigned' ? { awaitingAssignment: true } : { allOrganizations: true, includeUnassigned: show === 'all' }),
      query: term,
      integrityFindingsOnly: flaggedOnly,
      ...page,
    }),
    noun: 'devices',
  });
  const rows = list.query.data?.devices ?? [];
  // The numbers are of every device the list could show, revoked ones aside, as it lists them.
  const counts = useQuery(deviceCountsQuery('', true)).data;
  const groups = (counts ?? []).filter((g) => !g.revoked);
  const inView = groups.filter((g) => (show === 'unassigned' ? g.unassigned : show === 'assigned' ? !g.unassigned : true));
  const none = devicesIn(groups, (g) => g.unassigned);
  const flagged = devicesIn(inView, (g) => g.integrityFindings);
  const active = devicesIn(groups, (g) => !g.unassigned && g.status === DeviceStatus.ACTIVE);
  // As the page stands now, so the details follow a change made from them; read alone once it is on another.
  const onPage = opened ? rows.find((d) => d.id === opened) : undefined;
  const one = useQuery({ ...deviceQuery(opened), enabled: !!opened && !onPage });
  const shown = opened ? onPage ?? one.data ?? null : null;
  const pane = useShown(shown);
  return (
    <>
      <AcrossList
        title="All devices"
        description="Every device the console knows. One that is unassigned belongs to no organization, and nobody can sign in on it until it is assigned."
        noun="devices"
        icon={IconDeviceTablet}
        stats={counts ? [
          { label: 'Devices', value: devicesIn(groups), hint: 'Assigned and unassigned' },
          { label: 'Unassigned', value: none, hint: none ? 'Waiting for an organization' : 'Every device has an organization', tone: none ? 'warn' : 'neutral' },
          { label: 'Active', value: active, hint: 'Assigned, and operators can sign in' },
        ] : 'loading'}
        columns={COLUMNS}
        rows={rows}
        total={list.query.data ? list.total : undefined}
        of={counts ? (flaggedOnly ? flagged : devicesIn(inView)) : undefined}
        footer={list.footer}
        stale={list.query.isPlaceholderData}
        pending={list.query.isPending}
        error={list.query.error}
        q={q}
        onQ={setQ}
        searchLabel="Search devices"
        to="/devices"
        filter={(
          <Group gap="lg" wrap="wrap">
            <SegmentedControl
              aria-label="Which devices to show"
              value={show}
              onChange={(v) => setShow(v as Show)}
              data={[{ value: 'all', label: 'All' }, { value: 'unassigned', label: `Unassigned${none ? ` (${none})` : ''}` }, { value: 'assigned', label: 'Assigned' }]}
            />
            <Switch
              label={`Integrity findings only${flagged ? ` (${flagged})` : ''}`}
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.currentTarget.checked)}
            />
          </Group>
        )}
        empty={flaggedOnly ? {
          title: `No ${show === 'all' ? '' : `${show} `}device reports a finding`,
          body: 'A device is listed here when its own report shows something a release tablet in normal use would not. One that has not reported is not counted.',
        } : undefined}
        // A row opens the device's details, whichever kind it is; what can be done to it is on its menu.
        onOpen={(d) => { setOpened(d.id); return true; }}
        actions={(d) => <DeviceActions device={d} compact />}
        orgless={(d) => <OrglessBadge d={d} />}
      />
      <PaneDrawer opened={pane.opened} onClose={() => setOpened(null)}>
        {pane.shown && <DevicePane key={pane.shown.id} device={pane.shown} />}
      </PaneDrawer>
    </>
  );
}
